import { useState, useEffect } from 'react';
import { BookOpen, Plus, X, Check, Loader2, Sparkles, FileText, Video, Headphones, Globe } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import type { ModuleLiterature } from '@/types';
import { useToast } from './ui';

interface Literature {
  id: string;
  title: string;
  description?: string;
  literature_type: string;
  original_url?: string;
}

interface ModuleData {
  module_code: string;
  title: string;
  description?: string;
  ects_credits?: number;
  learning_outcomes?: {
    knowledge?: string[];
    skills?: string[];
    competence?: string[];
  };
}

interface ModuleLiteratureAssignerProps {
  studyPlanId: string;
  moduleCode: string;
  moduleId?: string;
  moduleData?: ModuleData;
  assignments: ModuleLiterature[];
  onChange: (assignments: ModuleLiterature[]) => void;
  disabled?: boolean;
}

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'video':
      return <Video size={14} className="text-red-500" />;
    case 'audio':
    case 'podcast':
      return <Headphones size={14} className="text-purple-500" />;
    case 'webpage':
    case 'website':
      return <Globe size={14} className="text-kf-blue-500" />;
    default:
      return <FileText size={14} className="text-gray-500" />;
  }
};

export default function ModuleLiteratureAssigner({
  studyPlanId,
  moduleCode,
  moduleId: _moduleId,
  moduleData,
  assignments,
  onChange,
  disabled = false,
}: ModuleLiteratureAssignerProps) {
  const toast = useToast();
  const [availableLiterature, setAvailableLiterature] = useState<Literature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<ModuleLiterature | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  // Form state for editing/adding
  const [formData, setFormData] = useState<Partial<ModuleLiterature>>({
    literature_id: '',
    page_range: '',
    chapters: '',
    sections: '',
    time_range: '',
    is_required: true,
    usage_note: '',
  });

  useEffect(() => {
    loadAvailableLiterature();
  }, [studyPlanId]);

  const loadAvailableLiterature = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`/api/study-plans/${studyPlanId}/literature`);
      const data = await response.json();
      if (data.success) {
        setAvailableLiterature(data.literature || []);
      }
    } catch (err) {
      console.error('Error loading literature:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssignment = () => {
    if (!formData.literature_id) return;

    const literature = availableLiterature.find(l => l.id === formData.literature_id);
    if (!literature) return;

    const newAssignment: ModuleLiterature = {
      literature_id: formData.literature_id,
      literature_title: literature.title,
      literature_type: literature.literature_type,
      page_range: formData.page_range || undefined,
      chapters: formData.chapters || undefined,
      sections: formData.sections || undefined,
      time_range: formData.time_range || undefined,
      is_required: formData.is_required ?? true,
      usage_note: formData.usage_note || undefined,
    };

    onChange([...assignments, newAssignment]);
    resetForm();
    setShowAddModal(false);
  };

  const handleUpdateAssignment = () => {
    if (!editingAssignment) return;

    const updatedAssignments = assignments.map(a =>
      a.literature_id === editingAssignment.literature_id
        ? {
            ...a,
            page_range: formData.page_range || undefined,
            chapters: formData.chapters || undefined,
            sections: formData.sections || undefined,
            time_range: formData.time_range || undefined,
            is_required: formData.is_required ?? true,
            usage_note: formData.usage_note || undefined,
          }
        : a
    );

    onChange(updatedAssignments);
    resetForm();
    setEditingAssignment(null);
  };

  const handleRemoveAssignment = (literatureId: string) => {
    onChange(assignments.filter(a => a.literature_id !== literatureId));
  };

  const handleEditAssignment = (assignment: ModuleLiterature) => {
    setFormData({
      literature_id: assignment.literature_id,
      page_range: assignment.page_range || '',
      chapters: assignment.chapters || '',
      sections: assignment.sections || '',
      time_range: assignment.time_range || '',
      is_required: assignment.is_required,
      usage_note: assignment.usage_note || '',
    });
    setEditingAssignment(assignment);
  };

  const handleSuggestLiterature = async () => {
    if (!studyPlanId) return;

    setSuggesting(true);
    try {
      // Use moduleData from props if available, otherwise fetch from database
      let currentModule = moduleData;

      if (!currentModule) {
        // Fallback: fetch from database
        const modulesResponse = await authenticatedFetch(`/api/ai/modules/${studyPlanId}`);
        const modulesData = await modulesResponse.json();

        if (!modulesData.success || !modulesData.modules) {
          throw new Error('Could not load modules');
        }

        currentModule = modulesData.modules.find((m: any) => m.module_code === moduleCode);
        if (!currentModule) {
          throw new Error('Module not found. Lagre studieplanen først.');
        }
      }

      // Call AI to suggest literature for this module
      const response = await authenticatedFetch(`/api/ai/modules/literature`, {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: studyPlanId,
          modules: [currentModule],
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Could not generate suggestions');
      }

      const data = await response.json();

      // Check if no literature available
      if (!data.assignments || data.assignments.length === 0) {
        toast.showWarning(data.message || 'Ingen litteratur tilgjengelig. Legg til litteratur i "Litteratur"-fanen først.');
        return;
      }

      // Apply suggestions for this module
      const moduleAssignments = data.assignments.find(
        (a: any) => a.module_code === moduleCode
      );

      if (moduleAssignments?.literature && moduleAssignments.literature.length > 0) {
        // Map suggestions to ModuleLiterature format
        const newAssignments: ModuleLiterature[] = moduleAssignments.literature.map((lit: any) => ({
          literature_id: lit.literature_id,
          literature_title: lit.title,
          page_range: lit.page_range || undefined,
          chapters: lit.chapters || undefined,
          sections: lit.sections || undefined,
          time_range: lit.time_range || undefined,
          is_required: lit.is_required ?? true,
          usage_note: lit.usage_note || undefined,
        }));

        // Merge with existing, avoiding duplicates
        const existingIds = new Set(assignments.map(a => a.literature_id));
        const uniqueNew = newAssignments.filter(a => !existingIds.has(a.literature_id));
        onChange([...assignments, ...uniqueNew]);
      } else {
        toast.showInfo('KI fant ingen passende litteratur for denne modulen.');
      }
    } catch (err: any) {
      console.error('Error suggesting literature:', err);
      toast.showError('Kunne ikke generere forslag: ' + err.message);
    } finally {
      setSuggesting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      literature_id: '',
      page_range: '',
      chapters: '',
      sections: '',
      time_range: '',
      is_required: true,
      usage_note: '',
    });
  };

  // Get unassigned literature
  const unassignedLiterature = availableLiterature.filter(
    lit => !assignments.some(a => a.literature_id === lit.id)
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 size={14} className="animate-spin" />
        <span className="text-sm">Laster litteratur...</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <BookOpen size={14} />
          Litteratur for denne modulen
        </h4>
        <button
          type="button"
          onClick={handleSuggestLiterature}
          className="text-xs flex items-center gap-1 text-primary-600 hover:text-primary-800"
          disabled={suggesting || disabled}
        >
          {suggesting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Foreslå med KI
        </button>
      </div>

      {/* Assigned literature list */}
      {assignments.length > 0 ? (
        <div className="space-y-2">
          {assignments.map(assignment => (
            <div
              key={assignment.literature_id}
              className="bg-white border rounded p-2 text-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(assignment.literature_type || 'pdf')}
                    <span className="font-medium">{assignment.literature_title}</span>
                    {assignment.is_required ? (
                      <span className="text-xs bg-kf-blue-100 text-kf-blue-700 px-1 rounded">Obligatorisk</span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-600 px-1 rounded">Anbefalt</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 space-x-2">
                    {assignment.page_range && <span>s. {assignment.page_range}</span>}
                    {assignment.chapters && <span>Kap. {assignment.chapters}</span>}
                    {assignment.sections && <span>Sek. {assignment.sections}</span>}
                    {assignment.time_range && <span>{assignment.time_range}</span>}
                  </div>
                  {assignment.usage_note && (
                    <p className="text-xs text-gray-600 italic mt-1">{assignment.usage_note}</p>
                  )}
                </div>
                {!disabled && (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => handleEditAssignment(assignment)}
                      className="text-kf-blue-600 hover:text-kf-blue-800 p-1"
                    >
                      Rediger
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveAssignment(assignment.literature_id)}
                      className="text-red-600 hover:text-red-800 p-1"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">Ingen litteratur tildelt denne modulen ennå.</p>
      )}

      {/* Add button or message when no literature */}
      {!disabled && !showAddModal && !editingAssignment && (
        <>
          {unassignedLiterature.length > 0 ? (
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="text-sm flex items-center gap-1 text-primary-600 hover:text-primary-800"
            >
              <Plus size={14} />
              Legg til litteratur
            </button>
          ) : availableLiterature.length === 0 ? (
            <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
              <p className="font-medium">Ingen litteratur tilgjengelig</p>
              <p className="text-xs mt-1">
                Gå til "Litteratur"-fanen for å legge til litteratur til studieplanen først.
                Du kan legge til manuelt, importere fra delt bibliotek, eller bruke KI-forslag.
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500">All litteratur er allerede tildelt denne modulen.</p>
          )}
        </>
      )}

      {/* Add/Edit form */}
      {(showAddModal || editingAssignment) && !disabled && (
        <div className="bg-gray-50 p-3 rounded border space-y-3">
          <h5 className="font-medium text-sm">
            {editingAssignment ? 'Rediger litteraturreferanse' : 'Legg til litteratur'}
          </h5>

          {/* Literature selector (only for new) */}
          {!editingAssignment && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Velg litteratur</label>
              <select
                className="input w-full text-sm"
                value={formData.literature_id}
                onChange={e => setFormData({ ...formData, literature_id: e.target.value })}
              >
                <option value="">-- Velg --</option>
                {unassignedLiterature.map(lit => (
                  <option key={lit.id} value={lit.id}>
                    {lit.title}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Page range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Sideområde</label>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="f.eks. 1-60, 78-92"
                value={formData.page_range || ''}
                onChange={e => setFormData({ ...formData, page_range: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Kapitler</label>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="f.eks. 1-3, 5"
                value={formData.chapters || ''}
                onChange={e => setFormData({ ...formData, chapters: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Seksjoner</label>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="f.eks. 1.1, 2.3-2.5"
                value={formData.sections || ''}
                onChange={e => setFormData({ ...formData, sections: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Tidsområde (video/lyd)</label>
              <input
                type="text"
                className="input w-full text-sm"
                placeholder="f.eks. 00:00-15:30"
                value={formData.time_range || ''}
                onChange={e => setFormData({ ...formData, time_range: e.target.value })}
              />
            </div>
          </div>

          {/* Required toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_required"
              checked={formData.is_required}
              onChange={e => setFormData({ ...formData, is_required: e.target.checked })}
            />
            <label htmlFor="is_required" className="text-sm">Obligatorisk litteratur</label>
          </div>

          {/* Usage note */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Bruksnotat (valgfritt)</label>
            <input
              type="text"
              className="input w-full text-sm"
              placeholder="f.eks. Fokuser på eksemplene i kapittel 2"
              value={formData.usage_note || ''}
              onChange={e => setFormData({ ...formData, usage_note: e.target.value })}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="btn btn-secondary text-sm"
              onClick={() => {
                resetForm();
                setShowAddModal(false);
                setEditingAssignment(null);
              }}
            >
              Avbryt
            </button>
            <button
              type="button"
              className="btn btn-primary text-sm flex items-center gap-1"
              onClick={editingAssignment ? handleUpdateAssignment : handleAddAssignment}
              disabled={!editingAssignment && !formData.literature_id}
            >
              <Check size={14} />
              {editingAssignment ? 'Oppdater' : 'Legg til'}
            </button>
          </div>
        </div>
      )}

      {/* No available literature message */}
      {unassignedLiterature.length === 0 && assignments.length === 0 && (
        <p className="text-sm text-gray-500">
          Ingen litteratur lagt til studieplanen ennå. Legg til litteratur i Litteratur-seksjonen først.
        </p>
      )}
    </div>
  );
}
